/**
 * MetricAggregationCacheInvalidator
 *
 * Single owner of rebuild-driven cache invalidation and account-lifecycle
 * purging for the persistent metric aggregation cache (metrics-part-2
 * sub-plan 005, task 002).
 *
 * Mounted once inside `CoreProviders` under `AppSessionProvider` and
 * `GroupSubscriptionProvider`, so graph components never add rebuild listeners
 * themselves. The React component is a thin wrapper over
 * {@link createRebuildInvalidator}, a framework-free controller that is unit
 * tested directly with controlled async deps.
 *
 * Nothing here logs descriptors, subjects, series or metric values.
 */

import { useEffect, useRef } from 'react';
import { useUser } from '@/hooks/useUser';
import { useServiceEventListener, useServiceGroup } from '@/hooks/useService';
import {
  RPC_EVENT_METRIC_REBUILD_PROGRESS_IDENT,
  METRIC_REBUILD_GROUP_TYPE,
  METRIC_REBUILD_GROUP_ID,
} from '@bloomstack/apis/services/metrics/metricAggregationTypes';
import {
  clearMetricAggregationCache,
  invalidatePipeline,
  metricAggregationDb,
  purgeOtherViewers,
  purgeViewers,
} from '@/db/metricAggregationDatabase';

// ─── Core controller (exported for tests) ─────────────────────────────────────

export type RebuildInvalidatorDeps = {
  /** Bump a pipeline's generation and delete its datasets. */
  invalidatePipeline: (viewerUserId: string, pipelineId: string) => Promise<void>;
  /** Purge the named viewers entirely. */
  purgeViewers: (viewerUserIds: string[]) => Promise<void>;
  /** Purge every viewer except the named one. */
  purgeOtherViewers: (viewerUserId: string) => Promise<void>;
};

type TrackedPipeline = {
  /** Initial (first non-terminal) invalidation still running. */
  invalidating: boolean;
  /** A terminal event arrived while the initial invalidation was pending. */
  terminalQueued: boolean;
};

export type RebuildInvalidator = {
  /** Establish/clear the current viewer, purging other accounts' data. */
  setViewer: (viewerUserId: string | undefined) => void;
  /** Handle a rebuild progress event. */
  handleProgress: (pipelineId: string, percent: number, done: boolean) => void;
  /** Current viewer id (for assertions). */
  viewerId: () => string | undefined;
  /** Number of pipelines currently tracked as mid-rebuild. */
  trackedCount: () => number;
};

/**
 * Framework-free invalidation controller.
 *
 * Rebuild lifecycle contract:
 * - First observed `done=false`: mark active synchronously and invalidate once
 *   so pre-rebuild cache is not presented as current.
 * - Subsequent progress percentages: ignored (generation bumps once per run).
 * - `done=true`: invalidate again so partial rebuild data is removed. A terminal
 *   event received while the initial invalidation is still pending schedules
 *   exactly one terminal invalidation after it.
 * - A terminal-only event (first observation is `done=true`) invalidates once.
 *
 * Account lifecycle contract:
 * - Viewer A established: purge every other viewer.
 * - Viewer A -> undefined: purge A (unexpected auth loss).
 * - Viewer A -> B: `purgeOtherViewers(B)` removes A's data.
 */
export function createRebuildInvalidator(deps: RebuildInvalidatorDeps): RebuildInvalidator {
  let viewerId: string | undefined;
  const tracked = new Map<string, TrackedPipeline>();

  const runTerminal = (viewer: string, pipelineId: string): void => {
    tracked.delete(pipelineId);
    void deps.invalidatePipeline(viewer, pipelineId).catch(() => { /* silent */ });
  };

  const runInitial = (viewer: string, pipelineId: string): void => {
    const entry: TrackedPipeline = { invalidating: true, terminalQueued: false };
    tracked.set(pipelineId, entry);
    void deps.invalidatePipeline(viewer, pipelineId)
      .catch(() => { /* silent */ })
      .finally(() => {
        entry.invalidating = false;
        if (entry.terminalQueued) {
          entry.terminalQueued = false;
          // The run already reached terminal while we waited — invalidate again.
          if (tracked.get(pipelineId) === entry) {
            runTerminal(viewer, pipelineId);
          }
        }
      });
  };

  return {
    setViewer(next) {
      if (next === viewerId) return;
      const previous = viewerId;
      viewerId = next;
      // Reset active-pipeline tracking on any viewer change.
      tracked.clear();

      if (next) {
        void deps.purgeOtherViewers(next).catch(() => { /* silent */ });
      } else if (previous) {
        void deps.purgeViewers([previous]).catch(() => { /* silent */ });
      }
    },

    handleProgress(pipelineId, _percent, done) {
      const viewer = viewerId;
      if (!viewer) return;

      if (!done) {
        // First non-terminal observation starts a run; intermediates are ignored.
        if (!tracked.has(pipelineId)) {
          runInitial(viewer, pipelineId);
        }
        return;
      }

      const entry = tracked.get(pipelineId);
      if (!entry) {
        // Terminal-only event — no prior progress observed.
        runTerminal(viewer, pipelineId);
        return;
      }
      if (entry.invalidating) {
        // Defer exactly one terminal invalidation until the initial one settles.
        entry.terminalQueued = true;
        return;
      }
      runTerminal(viewer, pipelineId);
    },

    viewerId: () => viewerId,
    trackedCount: () => tracked.size,
  };
}

// ─── Production deps ──────────────────────────────────────────────────────────

const productionDeps: RebuildInvalidatorDeps = {
  invalidatePipeline: (viewerUserId, pipelineId) => invalidatePipeline(metricAggregationDb, viewerUserId, pipelineId),
  purgeViewers: (viewerUserIds) => purgeViewers(metricAggregationDb, viewerUserIds),
  purgeOtherViewers: (viewerUserId) => purgeOtherViewers(metricAggregationDb, viewerUserId),
};

/**
 * Explicit clear for the logout path. Awaited before the redirecting Keycloak
 * logout, because the normal logout redirect can occur before a React
 * cleanup/effect runs.
 */
export async function clearMetricCacheForLogout(viewerUserId: string | undefined): Promise<void> {
  if (!viewerUserId) return;
  await clearMetricAggregationCache(viewerUserId);
}

// ─── Mounted component ────────────────────────────────────────────────────────

/**
 * Mounts {@link createRebuildInvalidator} once. Renders nothing.
 */
export function MetricAggregationCacheInvalidator(): null {
  const [viewer] = useUser();
  const viewerId = viewer?.id;

  const controllerRef = useRef<RebuildInvalidator | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = createRebuildInvalidator(productionDeps);
  }

  // Keep the controller's viewer in sync (purges other viewers on change).
  useEffect(() => {
    controllerRef.current!.setViewer(viewerId);
  }, [viewerId]);

  // Request the existing metric-rebuild group once a viewer is present.
  useServiceGroup(METRIC_REBUILD_GROUP_TYPE, viewerId ? METRIC_REBUILD_GROUP_ID : null);

  // One rebuild listener for the whole app; viewer id participates in deps so
  // the listener rebinds on viewer change.
  useServiceEventListener<(pipelineId: string, percent: number, done: boolean) => void>(
    RPC_EVENT_METRIC_REBUILD_PROGRESS_IDENT,
    (pipelineId, percent, done) => {
      controllerRef.current!.handleProgress(pipelineId, percent, done);
    },
    [viewerId],
  );

  return null;
}
